// Components
import { AccentCheckbox } from "../../../../shared/components/checkbox/AccentCheckbox";
import { Modal } from "../../../../shared/components/modal/Modal";
import { ModalContentFadeIn } from "../../../../shared/components/modal/ModalContentFadeIn";
import { CompactButton } from "../../../../shared/components/buttons/compact-button/CompactButton";
import { CircleLoader } from "../../../../shared/components/circle-loader/CircleLoader";

import { UserPromptSettingsBlock } from "./components/user-prompt-settings-block/UserPromptSettingsBlock";
import type { HeaderReportInfo } from "./useHeaderModals";

type Props = {
	// docs
	isDocsOpen: boolean;
	isDocsLoading: boolean;
	docsError: string | null;
	docsSafeHtml: string;
	onCloseDocs: () => void;

	// report
	isReportOpen: boolean;
	isReportLoading: boolean;
	reportInfo: HeaderReportInfo | null;
	reportError: string | null;
	onCloseReport: () => void;
	onDownloadReport: () => Promise<void>;
	formatFileSize: (bytes: number) => string;
	formatDateTime: (iso: string) => string;

	// settings
	isSettingsOpen: boolean;
	isSettingsLoading: boolean;
	isSettingsApplying: boolean;
	isSettingsApplyDisabled: boolean;
	buyerPreferencesLoaded: boolean;
	settingsError: string | null;
	autoAcceptApps: boolean;
	autoOverwriteCert: boolean;
	settingsLastUpdatedAt: string | null;
	isChatConnected: boolean;
	userPromptDraft: string;
	userPromptError: string | null;
	onCloseSettings: () => void;
	onAutoAcceptAppsDraftChange: (next: boolean) => void;
	onAutoOverwriteCertDraftChange: (next: boolean) => void;
	onUserPromptDraftChange: (next: string) => void;
	onApplySettings: () => Promise<void>;

	// auth error
	isAuthErrorOpen: boolean;
	onCloseAuthError: () => void;

	// logout confirm
	isLogoutConfirmOpen: boolean;
	isLogoutLoading: boolean;
	logoutError: string | null;
	onCloseLogoutConfirm: () => void;
	onConfirmLogout: () => Promise<void>;
};

export const HeaderModals = ({
	isDocsOpen,
	isDocsLoading,
	docsError,
	docsSafeHtml,
	onCloseDocs,

	isReportOpen,
	isReportLoading,
	reportInfo,
	reportError,
	onCloseReport,
	onDownloadReport,
	formatFileSize,
	formatDateTime,

	isSettingsOpen,
	isSettingsLoading,
	isSettingsApplying,
	isSettingsApplyDisabled,
	buyerPreferencesLoaded,
	settingsError,
	autoAcceptApps,
	autoOverwriteCert,
	settingsLastUpdatedAt,
	isChatConnected,
	userPromptDraft,
	userPromptError,
	onCloseSettings,
	onAutoAcceptAppsDraftChange,
	onAutoOverwriteCertDraftChange,
	onUserPromptDraftChange,
	onApplySettings,

	isAuthErrorOpen,
	onCloseAuthError,

	isLogoutConfirmOpen,
	isLogoutLoading,
	logoutError,
	onCloseLogoutConfirm,
	onConfirmLogout,
}: Props) => {
	return (
		<>
			<Modal
				isOpen={isReportOpen}
				onClose={onCloseReport}
				title='Звіт готовий до завантаження'
				size='md'>
				{isReportLoading && (
					<div className='header-modal__loader-wrapper'>
						<CircleLoader />
					</div>
				)}
				{!isReportLoading && reportError && (
					<ModalContentFadeIn className='header-modal__hint'>
						{reportError}
					</ModalContentFadeIn>
				)}
				{!isReportLoading && reportInfo && (
					<ModalContentFadeIn className='header-modal__rows'>
						<div className='header-modal__row'>
							<span className='header-modal__label'>Назва файлу:</span>
							<span>{reportInfo.file_name ?? "—"}</span>
						</div>
						<div className='header-modal__row'>
							<span className='header-modal__label'>Розмір файлу:</span>
							<span>
								{typeof reportInfo.file_size === "number"
									? formatFileSize(reportInfo.file_size)
									: "—"}
							</span>
						</div>
						<div className='header-modal__row'>
							<span className='header-modal__label'>Дата зміни:</span>
							<span>
								{reportInfo.last_modified
									? formatDateTime(reportInfo.last_modified)
									: "—"}
							</span>
						</div>
					</ModalContentFadeIn>
				)}
				<div className='header-modal__actions'>
					<CompactButton
						onClick={() => void onDownloadReport()}
						type='button'
						unactive={isReportLoading}
						theme='default'>
						Завантажити
					</CompactButton>
				</div>
			</Modal>

			<Modal
				isOpen={isDocsOpen}
				onClose={onCloseDocs}
				title='Документація'
				size='docs'>
				{isDocsLoading ? (
					<div className='header-modal__loader-wrapper'>
						<CircleLoader />
					</div>
				) : docsError ? (
					<ModalContentFadeIn className='header-modal__hint'>
						{docsError}
					</ModalContentFadeIn>
				) : (
					<ModalContentFadeIn
						key={docsSafeHtml.slice(0, 64)}
						className='header-modal__docs'>
						<div
							dangerouslySetInnerHTML={{
								__html: docsSafeHtml || "<p>—</p>",
							}}
						/>
					</ModalContentFadeIn>
				)}
			</Modal>

			<Modal
				isOpen={isSettingsOpen}
				onClose={onCloseSettings}
				title='Налаштування'
				size='lg'>
				{isSettingsLoading ? (
					<div className='header-modal__loader-wrapper'>
						<CircleLoader />
					</div>
				) : (
					<ModalContentFadeIn className='header-modal__settings'>
						{settingsError && (
							<div className='header-modal__hint'>{settingsError}</div>
						)}
						{buyerPreferencesLoaded && (
							<>
								<AccentCheckbox
									checked={autoAcceptApps}
									onChange={(checked) => onAutoAcceptAppsDraftChange(checked)}>
									Авто прийом застосунків
								</AccentCheckbox>
								<AccentCheckbox
									checked={autoOverwriteCert}
									onChange={(checked) =>
										onAutoOverwriteCertDraftChange(checked)
									}>
									Авто оновлення сертифікатів
								</AccentCheckbox>
								{settingsLastUpdatedAt && (
									<div className='header-modal__hint'>
										Останнє оновлення: {formatDateTime(settingsLastUpdatedAt)}
									</div>
								)}
								{isChatConnected && (
									<UserPromptSettingsBlock
										value={userPromptDraft}
										error={userPromptError}
										disabled={isSettingsApplying}
										onChange={onUserPromptDraftChange}
									/>
								)}
							</>
						)}
					</ModalContentFadeIn>
				)}
				<div className='header-modal__actions'>
					<CompactButton
						onClick={onCloseSettings}
						type='button'
						theme='default'>
						Скасувати
					</CompactButton>
					<CompactButton
						onClick={() => void onApplySettings()}
						type='button'
						unactive={isSettingsApplyDisabled}
						theme='default'>
						{isSettingsApplying ? "Збереження..." : "Застосувати"}
					</CompactButton>
				</div>
			</Modal>

			<Modal
				isOpen={isAuthErrorOpen}
				onClose={onCloseAuthError}
				title='Помилка авторизації'
				size='sm'>
				<p className='header-modal__hint' style={{ margin: 0 }}>
					Доступ заборонено. Перевірте права доступу.
				</p>
				<div className='header-modal__actions'>
					<CompactButton
						onClick={onCloseAuthError}
						type='button'
						theme='default'>
						ОК
					</CompactButton>
				</div>
			</Modal>

			<Modal
				isOpen={isLogoutConfirmOpen}
				onClose={onCloseLogoutConfirm}
				title='Вийти з акаунту?'
				size='sm'>
				<p className='header-modal__hint' style={{ margin: 0 }}>
					Ви впевнені, що хочете вийти?
				</p>
				{logoutError && <div className='header-modal__hint'>{logoutError}</div>}
				<div className='header-modal__actions'>
					<CompactButton
						onClick={onCloseLogoutConfirm}
						type='button'
						theme='default'>
						Скасувати
					</CompactButton>
					<CompactButton
						onClick={() => void onConfirmLogout()}
						type='button'
						unactive={isLogoutLoading}
						theme='red'>
						Вийти
					</CompactButton>
				</div>
			</Modal>
		</>
	);
};
